import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import IconPlayCircle from '@/assets/icons/icon-play-circle.svg?react';

interface NodeVideoPlaceholderProps {
  isActive?: boolean;
}

const NodeVideoPlaceholder: React.FC<NodeVideoPlaceholderProps> = ({ isActive = false }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [showText, setShowText] = useState(true);
  const [iconSizePercent, setIconSizePercent] = useState(38.5);
  const [corner, setCorner] = useState<{ rx: number; ry: number }>({ rx: 0, ry: 0 });
  const [hovered, setHovered] = useState(false);

  const getIconSizePercent = (width: number) => {
    if (width < 158) return 90;
    if (width < 175) return 75;
    if (width < 257) return 50;
    return 38.5;
  };

  // Resize observer for outer container (text/icon sizing)
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        setShowText(width >= 257);
        setIconSizePercent(getIconSizePercent(width));
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Resize observer for inner container (border radius)
  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const measure = () => {
      const width = el.clientWidth;
      const height = el.clientHeight;
      if (!width || !height) return;
      const cs = getComputedStyle(el);
      const br = parseFloat(cs.borderTopLeftRadius || '0') || 0;
      setCorner({ rx: (br / width) * 100, ry: (br / height) * 100 });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const isDashed = !isActive;

  return (
    <div
      ref={containerRef}
      className={clsx(
        'group relative inline-flex h-full max-h-full w-full max-w-full flex-col items-center justify-center bg-[#FDE8E8] transition-all',
      )}
    >
      <div className="flex flex-1 flex-col items-center justify-center gap-2.5 self-stretch overflow-hidden p-4">
        <div
          ref={innerRef}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          className={clsx(
            `relative box-border flex h-full max-h-56 w-full max-w-60 flex-col items-center justify-center gap-4 overflow-hidden rounded-xl p-4 transition-all`,
            showText ? 'aspect-[4/3]' : 'aspect-square',
          )}
          style={{ backgroundColor: hovered ? '#F8D4D4' : undefined }}
        >
          {/* SVG border: dashed -> solid on hover */}
          <svg
            className="pointer-events-none absolute inset-0 h-full w-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden
            style={{ color: '#EA6559' }}
          >
            {/* Dashed state */}
            <rect
              x="0.5"
              y="0.5"
              width="99"
              height="99"
              rx={corner.rx}
              ry={corner.ry}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.1"
              vectorEffect="non-scaling-stroke"
              shapeRendering="crispEdges"
              strokeDasharray={isDashed ? '6 4' : undefined}
              style={{ opacity: isDashed && !hovered ? 1 : 0, transition: 'opacity 150ms ease' }}
            />
            {/* Solid state */}
            <rect
              x="0.5"
              y="0.5"
              width="99"
              height="99"
              rx={corner.rx}
              ry={corner.ry}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.1"
              vectorEffect="non-scaling-stroke"
              shapeRendering="crispEdges"
              style={{ opacity: hovered || !isDashed ? 1 : 0, transition: 'opacity 150ms ease' }}
            />
          </svg>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              aspectRatio: '1 / 1',
              width: `${iconSizePercent}%`,
              borderRadius: '0.75rem',
              backgroundColor: 'rgba(234, 101, 89, 0.10)',
              border: '1px solid #EA6559',
              transition: 'all 150ms ease',
              backdropFilter: 'blur(3.2px)',
            }}
          >
            <IconPlayCircle style={{ width: '50%', height: '50%', color: '#EA6559' }} />
          </div>

          {showText && (
            <p
              className="text-heading-ts w-full overflow-hidden text-center text-gray-600"
              style={{ fontSize: 'clamp(12px, 2vw, 12px)' }}
            >
              Paste a video URL in the
              <br />
              Inspector Video
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default NodeVideoPlaceholder;
